import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import React, { useCallback, useMemo } from "react";
import { MdsIcon } from "@sinch/components";
import { MdsLinkXs } from "@sinch/icons";
import { useMessageSend } from "@sinch/components/application/Messages/useMessageSend";
import { useReconfirmationDialog } from "@sinch/components/application/Messages/MessageForm/useReconfirmationDialog";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { Placeholders } from "@sinch/components/application/Messages/MessageForm/MessageForm";
import { mapObjIndexed } from "ramda";

const RECONFIRMATION_LINK_PLACEHOLDER_KEY = "reconfirmationLink";

interface UseMessageFormProps {
  defaultOpened?: NotificationChannel;

  /**
   * List of recipient IDs.
   */
  recipientsIds?: string[];
  /**
   * The type of the related object.
   */
  relatedObject?: "order" | "job" | "shift" | "position";
  /**
   * ID of the related object.
   */
  relatedObjectId?: string;

  /**
   * Allowed channels for messaging.
   */
  allowedChannels: NotificationChannel[];
  reconfirmationLinkLength?: number;
  mode?: MessageFormMode;
  onSuccess?: () => void;
  attendances?: {
    id: string;
    userId: string;
  }[];
  placeholders?: Partial<Record<NotificationChannel, Placeholders>>;
}

export const useMessageForm = ({
  reconfirmationLinkLength,
  recipientsIds,
  relatedObject,
  relatedObjectId,
  mode = MessageFormMode.Default,
  attendances,
  onSuccess: handleSuccess,
  defaultOpened,
  placeholders = {},
}: UseMessageFormProps) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  /**
   * Callback fired on successful message sending.
   */
  const onSuccess = useCallback(
    (message?: string) => {
      if (message) {
        enqueueSnackbar(message, { variant: "success" });
      }
      handleSuccess?.();
    },
    [enqueueSnackbar, handleSuccess]
  );

  const customPlaceholders = useMemo(
    () =>
      reconfirmationLinkLength && mode === MessageFormMode.Reconfirmation
        ? mapObjIndexed(
            (plcs) => [
              {
                value: RECONFIRMATION_LINK_PLACEHOLDER_KEY,
                label: (
                  <>
                    <MdsIcon fontSize="xs" icon={MdsLinkXs} sx={{ mr: 0.5 }} />
                    {t("MessageDialog.insertReconfirmLink")}
                  </>
                ),
                size: reconfirmationLinkLength,
              },
              ...plcs,
            ],
            placeholders
          )
        : placeholders,
    [reconfirmationLinkLength, mode]
  );

  const messageForm = useMessageSend({
    initRecipients: recipientsIds ?? [],
    relatedObject: relatedObject,
    relatedObjectId: relatedObjectId,
    onSuccess: () => onSuccess(t("Messages.sent")),
    placeholders: customPlaceholders,
    defaultOpened,
  });
  const reconfirmationForm = useReconfirmationDialog({
    attendanceIds: attendances,
    onSuccess: () => {
      onSuccess();
      globalThis.location.reload();
    },
    placeholders: customPlaceholders,
    defaultOpened,
  });

  const { form, handleSubmit } = mode === MessageFormMode.Reconfirmation ? reconfirmationForm : messageForm;

  return { form, onSubmit: handleSubmit, placeholders: customPlaceholders };
};

/**
 * Enum representing the mode of the message form.
 */
export enum MessageFormMode {
  Reconfirmation = "reconfirmation",
  Default = "default",
}
